import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { useEffect, useState, useRef } from 'react';
import { auth } from '../core/firebaseConfig';
import * as Sentry from '@sentry/react';

export const useOrdersListVendor = (locationId: number) => {
  const [orders, setOrders] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stompClient = useRef<Client | null>(null);
  const reconnectAttempts = useRef(0);

  useEffect(() => {
    if (stompClient.current && stompClient.current.connected) {
      console.error('Connection already exists!');
      return;
    }

    const connect = async () => {
      const token = await auth.currentUser?.getIdToken();
      const socket = new SockJS(`${process.env.REACT_APP_API_URL}/ws`);

      const client = new Client({
        webSocketFactory: () => socket,
        connectHeaders: {
          Authorization: `Bearer ${token}`,
        },

        onConnect: (_frame) => {
          setIsConnected(true);
          setError(null);
          reconnectAttempts.current = 0;
          console.log(`📡 Subscribingdd to: /topic/orders/list/${locationId}`);

          client.subscribe(`/topic/orders/list/${locationId}`, (message) => {
            try {
              const ordersList = JSON.parse(message.body);
              setOrders(ordersList);
            } catch (err) {
              console.error('Error parsing message:', err);
              setError('Failed to parse orders');
              Sentry.captureException(err);
            }
          });

          console.log('📡 Requesting current orders list...');
          client.publish({
            destination: '/app/orders/list/request',
            body: '',
          });
        },

        onDisconnect: () => {
          console.log('Websocket disconnected');
          setIsConnected(false);
        },

        onStompError: (frame) => {
          const message = frame.headers['message'] || 'Connection error';
          setError(message);
          setIsConnected(false);
          Sentry.captureException(new Error(`Vendor orders STOMP error: ${message}`), {
            extra: { body: frame.body, locationId },
          });
        },

        onWebSocketError: (event) => {
          console.error('⚠WebSocket error:', event);
          setError('WebSocket connection failed');
          Sentry.captureException(new Error('Vendor orders WebSocket connection failed'), {
            extra: { event: String(event), locationId },
          });
        },

        beforeConnect: async () => {
          reconnectAttempts.current += 1;
          console.log(`🔄 Reconnect attempt ${reconnectAttempts.current}`);

          if (reconnectAttempts.current > 2) {
            console.log('Max reconnect attempts reached');
            setError('Failed to connect after 2 attempts');
            client.deactivate();
            return;
          }

          const freshToken = await auth.currentUser?.getIdToken();
          client.connectHeaders = { Authorization: `Bearer ${freshToken}` };
        },

        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
      });

      client.activate();
      stompClient.current = client;
    };

    connect();

    return () => {
      if (stompClient.current) {
        stompClient.current.deactivate();
        stompClient.current = null;
      }
    };
  }, []);

  return { orders, isConnected, error };
};
